import { useMemo, useState } from 'react';

/**
 * Список с галочками для выдачи тестов / тренажёров / конспектов.
 * groups: [{ label, items: [{ id, title, meta?, disabled?, disabledLabel? }] }]
 */
export default function AssignCheckboxList({
  groups = [],
  selectedIds,
  onChange,
  searchPlaceholder = 'Поиск…',
  emptyText = 'Ничего не найдено.',
  disabled = false,
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();

  const filteredGroups = useMemo(() => {
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        items: (g.items || []).filter((item) => {
          const hay = `${item.title || ''} ${item.meta || ''}`.toLowerCase();
          return hay.includes(q);
        }),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, q]);

  const selectedSet = useMemo(() => new Set(selectedIds.map(String)), [selectedIds]);
  const selectableCount = useMemo(
    () => filteredGroups.reduce(
      (n, g) => n + g.items.filter((it) => !it.disabled).length,
      0,
    ),
    [filteredGroups],
  );
  const selectedCount = selectedIds.length;

  function toggle(id, itemDisabled) {
    if (disabled || itemDisabled) return;
    const key = String(id);
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(Array.from(next));
  }

  return (
    <div className="assign-checkbox-list">
      <input
        type="search"
        className="form-input assign-checkbox-search"
        placeholder={searchPlaceholder}
        value={query}
        disabled={disabled}
        onChange={(e) => setQuery(e.target.value)}
        aria-label={searchPlaceholder}
      />
      <div className="assign-checkbox-scroll" role="group" aria-label="Список для выбора">
        {filteredGroups.length === 0 ? (
          <p className="text-secondary assign-checkbox-empty">{emptyText}</p>
        ) : (
          filteredGroups.map((group) => (
            <div key={group.label || 'all'} className="assign-checkbox-group">
              {group.label ? (
                <div className="assign-checkbox-group-label">{group.label}</div>
              ) : null}
              <ul className="assign-checkbox-items">
                {group.items.map((item) => {
                  const id = String(item.id);
                  const checked = selectedSet.has(id) || Boolean(item.disabled);
                  return (
                    <li key={id}>
                      <label
                        className={
                          item.disabled
                            ? 'assign-checkbox-row is-disabled'
                            : 'assign-checkbox-row'
                        }
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={disabled || item.disabled}
                          onChange={() => toggle(item.id, item.disabled)}
                        />
                        <span className="assign-checkbox-row-main">
                          <span className="assign-checkbox-row-title">{item.title}</span>
                          {item.meta ? (
                            <span className="assign-checkbox-row-meta text-secondary">
                              {item.meta}
                            </span>
                          ) : null}
                          {item.disabled && item.disabledLabel ? (
                            <span className="assign-checkbox-row-badge text-secondary">
                              {item.disabledLabel}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))
        )}
      </div>
      <p className="text-secondary assign-checkbox-count" role="status">
        Выбрано: {selectedCount}
        {selectableCount > 0 ? ` из ${selectableCount}` : ''}
      </p>
    </div>
  );
}
